import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import {
  FaArrowRightLong,
  FaBullseye,
  FaCompass,
  FaLaptopCode,
  FaLink,
  FaNetworkWired,
  FaShieldHalved,
} from "react-icons/fa6";
import { IoSparklesOutline } from "react-icons/io5";
import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";
import banner7 from "../assets/banner6.jpg";
import logo from "../assets/logo.png";

export default function AboutUsContent() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);

  const banners = [banner5, banner7, banner1, banner2, banner3, banner4];
  const serviceAreas = [
    "ICT Consulting & Strategy",
    "Systems Integration",
    "Network Infrastructure Solutions",
    "IT Support & Maintenance",
    "Digital Transformation Solutions",
    "Software & Application Development",
  ];
  const promiseCards = [
    {
      icon: <FaCompass size={22} />,
      title: "Mission",
      copy:
        "To deliver innovative and reliable ICT solutions that meet the evolving needs of businesses and organizations while maintaining excellence, integrity, and continuous improvement.",
    },
    {
      icon: <FaBullseye size={22} />,
      title: "Vision",
      copy:
        "To be a leading ICT solutions provider recognized for excellence, integrity, and customer satisfaction in a future empowered by technology.",
    },
  ];
  const strengths = [
    {
      icon: <IoSparklesOutline size={22} />,
      title: "Innovation",
      copy:
        "Continuously embracing new technologies and creative ways to solve client challenges.",
    },
    {
      icon: <FaShieldHalved size={22} />,
      title: "Integrity",
      copy:
        "Working transparently, ethically, and with accountability throughout every project lifecycle.",
    },
    {
      icon: <FaLink size={22} />,
      title: "Customer Focus",
      copy:
        "Building long-term relationships through responsive support and relevant, business-led solutions.",
    },
    {
      icon: <FaLaptopCode size={22} />,
      title: "Excellence",
      copy:
        "Delivering high-quality systems and service experiences with consistency and precision.",
    },
  ];
  const outcomes = [
    "Enhance operational efficiency across teams and processes.",
    "Improve reliability and performance across critical systems.",
    "Enable more informed, data-driven decision-making.",
    "Support sustainable growth and long-term innovation.",
  ];

  useEffect(() => {
    setReady(false);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;

    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % banners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [ready, banners.length]);

  if (!ready) {
    return (
      <div style={{ display: 'flex', height: '100vh', width: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --primaryColor: #FFC511;
          --onPrimaryColor: #fefefe;
          --secondaryColor: #0089D8;
          --onSecondaryColor: #fefefe;
          --surfaceColor: #ffffff;
          --onSurfaceColor: #0c2d41;
          --backgroundColor: #ffffff;
          --onBackgroundColor: #0b2635;
          --extraColor: #e4c970;
          --font-main: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          --radius-lg: 24px;
          --radius-md: 16px;
          --radius-sm: 8px;
        }

        .tech-about-wrapper {
          font-family: var(--font-main);
          background-color: var(--backgroundColor);
          color: var(--onBackgroundColor);
          overflow-x: hidden;
          line-height: 1.6;
        }

        /* Hero */
        .tech-hero {
          position: relative;
          height: 85vh;
          min-height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .tech-hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .tech-hero-slide {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .tech-hero-slide.active {
          opacity: 1;
        }
        /* Dark overlay to ensure text readability on any image */
        .tech-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(11, 38, 53, 0.85), rgba(11, 38, 53, 0.95));
          z-index: 1;
        }
        .tech-hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          max-width: 900px;
          padding: 0 24px;
          color: var(--onSecondaryColor);
        }
        .tech-hero-logo {
          width: 64px;
          height: auto;
          margin-bottom: 2rem;
          /* Assuming logo is dark, invert it for light hero, or keep original if it's white. 
             Based on previous code, it was inverted for dark bg. 
             If logo is dark, we keep invert. If logo is white, we remove invert. 
             Let's assume standard logo might need adjustment. Keeping invert for safety on dark overlay. */
          filter: brightness(0) invert(1); 
          opacity: 0.95;
        }
        .tech-hero-title {
          font-size: 3rem;
          font-weight: 700;
          letter-spacing: -0.03em;
          margin: 0 0 1.5rem 0;
          color: #ffffff;
          line-height: 1.1;
        }
        @media (min-width: 768px) {
          .tech-hero-title {
            font-size: 4.5rem;
          }
        }
        .tech-hero-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto 2.5rem auto;
          font-weight: 400;
        }
        .tech-badge {
          display: inline-flex;
          align-items: center;
          padding: 8px 16px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          font-size: 0.85rem;
          font-weight: 600;
          color: #ffffff;
          backdrop-filter: blur(8px);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* Layout Utilities */
        .tech-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 6rem 24px;
        }
        .section-header {
          margin-bottom: 4rem;
          max-width: 800px;
        }
        .section-label {
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--secondaryColor);
          margin-bottom: 1rem;
          display: block;
        }
        .section-title {
          font-size: 2.25rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          margin: 0;
          color: var(--onBackgroundColor);
          line-height: 1.2;
        }

        /* Grid Cards (Mission/Vision) */
        .tech-grid-2 {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          margin-bottom: 6rem;
        }
        @media (min-width: 768px) {
          .tech-grid-2 {
            grid-template-columns: 1fr 1fr;
          }
        }
        .tech-card {
          background: var(--surfaceColor);
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-md);
          padding: 40px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        .tech-card:hover {
          border-color: var(--secondaryColor);
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .tech-card-icon {
          color: var(--secondaryColor);
          margin-bottom: 1.5rem;
          display: inline-flex;
          padding: 12px;
          background: rgba(0, 137, 216, 0.08);
          border-radius: 12px;
        }
        .tech-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 1rem 0;
          color: var(--onBackgroundColor);
        }
        .tech-card p {
          color: #4b5563;
          font-size: 1rem;
          line-height: 1.7;
          margin: 0;
        }

        /* Bento Grid for Strengths */
        .tech-bento-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1px;
          background: #e5e7eb;
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-md);
          overflow: hidden;
          margin-bottom: 6rem;
        }
        @media (min-width: 640px) {
          .tech-bento-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .tech-bento-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        .tech-bento-item {
          background: var(--surfaceColor);
          padding: 40px 32px;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          transition: background 0.3s ease;
        }
        .tech-bento-item:hover {
          background: #f9fafb;
        }
        .tech-bento-icon {
          color: var(--primaryColor);
          font-size: 1.5rem;
          /* Adding a subtle shadow to icon for pop */
          filter: drop-shadow(0 2px 4px rgba(255, 197, 17, 0.3));
        }
        .tech-bento-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--onBackgroundColor);
          margin: 0;
        }
        .tech-bento-copy {
          font-size: 0.95rem;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Services List */
        .tech-split-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 4rem;
          margin-bottom: 6rem;
          align-items: start;
        }
        @media (min-width: 900px) {
          .tech-split-layout {
            grid-template-columns: 1fr 1fr;
          }
        }
        .tech-service-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .tech-service-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 24px;
          background: var(--surfaceColor);
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-sm);
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--onBackgroundColor);
          transition: all 0.2s ease;
          cursor: default;
        }
        .tech-service-item:hover {
          border-color: var(--secondaryColor);
          background: rgba(0, 137, 216, 0.03);
          transform: translateX(4px);
          color: var(--secondaryColor);
        }
        .tech-service-item svg {
          color: #9ca3af;
          opacity: 0.5;
          transition: color 0.2s;
        }
        .tech-service-item:hover svg {
          color: var(--secondaryColor);
          opacity: 1;
        }

        /* CTA Section */
        .tech-cta-section {
          background: var(--onBackgroundColor);
          color: var(--surfaceColor);
          border-radius: var(--radius-lg);
          position: relative;
          overflow: hidden;
          box-shadow: 0 5px 10px -3.5px rgba(11, 38, 53, 0.25);
        }
        /* Subtle pattern or gradient overlay */
        .tech-cta-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, rgba(0, 137, 216, 0.15) 0%, transparent 70%);
          z-index: 0;
          pointer-events: none;
        }
        .tech-cta-content {
          position: relative;
          z-index: 1;
        }
        .tech-outcome-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin: 2.5rem 0;
        }
        @media (min-width: 640px) {
          .tech-outcome-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        .tech-outcome-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          color: #94a3b8;
          font-size: 0.95rem;
        }
        .tech-outcome-item svg {
          color: var(--primaryColor);
          margin-top: 4px;
          flex-shrink: 0;
        }
        .tech-btn-glow {
          background: var(--secondaryColor);
          color: var(--onSecondaryColor);
          border: none;
          padding: 16px 32px;
          border-radius: 99px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 14px 0 rgba(0, 137, 216, 0.39);
        }
        .tech-btn-glow:hover {
          background: #0077bd;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px 0 rgba(0, 137, 216, 0.23);
        }
        .tech-btn-glow svg {
          transition: transform 0.3s;
        }
        .tech-btn-glow:hover svg {
          transform: translateX(4px);
        }
      `}</style>

      <section className="tech-about-wrapper">
        {/* Hero */}
        <div className="tech-hero">
          <div className="tech-hero-bg">
            {banners.map((banner, idx) => (
              <div
                key={idx}
                className={`tech-hero-slide ${idx === bgIndex ? "active" : ""}`}
                style={{ backgroundImage: `url(${banner})` }}
              />
            ))}
          </div>
          <div className="tech-hero-overlay"></div>
          <div className="tech-hero-content" data-aos="fade-up">
            <h1 className="tech-hero-title">
              Reliable technology solutions for organizations ready to grow.
            </h1>
            <p className="tech-hero-subtitle">
              Nova Technologies Company Limited is a leading ICT solutions provider
              based in Dar es Salaam, Tanzania. We specialize in innovative,
              scalable, and reliable technology solutions.
            </p>
            <div className="tech-badge">
              Dar es Salaam, Tanzania
            </div>
          </div>
        </div>


        

        <div className="tech-container">

          {/* CTA / Commitment */}
          <div className="tech-cta-section p-3 p-md-5 py-4 mb-5" data-aos="fade-up">
            <div className="tech-cta-content">
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '3rem' }}>
                <div style={{ flex: '1 1 500px' }}>
                  <h2 className="section-title font-bold" style={{ fontSize: '2.5rem', marginBottom: '1rem', color: 'var(--secondaryColor)' }}>
                    Focused on measurable results and sustainable success.
                  </h2>
                  <div className="tech-outcome-grid">
                    {outcomes.map((item) => (
                      <div key={item} className="tech-outcome-item">
                        <FaArrowRightLong size={16} />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                  <button
                    className="tech-btn-glow"
                    onClick={() => appContext.navTo({ item: "contact_us" })}
                  >
                    Partner With Nova
                    <FaArrowRightLong size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="tech-grid-2">
            {promiseCards.map((card, idx) => (
              <div
                key={card.title}
                className="tech-card"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="tech-card-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </div>
            ))}
          </div>

          

          {/* Strengths (Bento Grid) */}
          <div className="section-header mt-5" data-aos="fade-up">
            <h2 className="section-title font-bold text-secondary">The principles that guide our code.</h2>
          </div>

          <div className="tech-bento-grid">
            {strengths.map((card, idx) => (
              <div
                key={card.title}
                className="tech-bento-item"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="tech-bento-icon">{card.icon}</div>
                <div>
                  <h4 className="tech-bento-title">{card.title}</h4>
                  <p className="tech-bento-copy">{card.copy}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Services Split */}
          <div className="tech-split-layout">
            <div data-aos="fade-right">
              <h2 className="section-title text-primary font-bold" style={{ marginBottom: '1.5rem' }}>
                Solutions designed around real business needs.
              </h2>
              <p style={{ color: '#4b5563', lineHeight: 1.7, maxWidth: '500px' }}>
                Nova Technologies combines technical expertise with a deep understanding
                of business operations to deliver systems that optimize performance,
                strengthen reliability, and support long-term digital maturity.
              </p>
            </div>
            <div className="tech-service-list" data-aos="fade-left">
              {serviceAreas.map((item, idx) => (
                <div key={item} className="tech-service-item" style={{ transitionDelay: `${idx * 50}ms` }}>
                  <span>{item}</span>
                  <FaNetworkWired size={16} />
                </div>
              ))}
            </div>
          </div>

          

        </div>
      </section>
    </>
  );
}